import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useIntl } from '@grandvisionhq/state';
import { getWeekDaysForWeeksFromDate, isDateBefore, shiftDate, isSameDay } from '../../utils/datetime.js';
import AvailabilityCalendar$1 from './AvailabilityCalendar.js';
import { useRangeByWeeks } from './hooks/use-range-by-weeks.js';
import { tileNameLabelsByWeeks, formatTileData, getLastWeekDay } from './utils.js';

const AvailabilityCalendarContainer = (_param)=>{
    var { selectedDate: _selectedDate , weekDate: _weekDate , weekCount =2 , closedDates , minEnabledDate: _minEnabledDate , maxEnabledDate , availability ={} , loading =false , noTimeSlots , onSelectDate , onChangeWeeks  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "selectedDate",
        "weekDate",
        "weekCount",
        "closedDates",
        "minEnabledDate",
        "maxEnabledDate",
        "availability",
        "loading",
        "noTimeSlots",
        "onSelectDate",
        "onChangeWeeks"
    ]);
    const weekStartOffset = 1 // Currently, the weeks will start on monday for all opcos. In the future this may need configurability.
    ;
    const { getLabel  } = useIntl();
    const today = React.useRef(new Date());
    const [weekDate, setWeekDate] = React.useState(_weekDate !== null && _weekDate !== void 0 ? _weekDate : today.current);
    const [weeks, setWeeks] = React.useState(getWeekDaysForWeeksFromDate(weekDate, weekCount, weekStartOffset));
    const [selectedDate, setSelectedDate] = React.useState(_selectedDate);
    const minEnabledDate = _minEnabledDate !== null && _minEnabledDate !== void 0 ? _minEnabledDate : today.current;
    const labels = React.useMemo(()=>({
            pastButton: getLabel('appointments.component.availabilityCalendar.ariaLabels.pastButton'),
            futureButton: getLabel('appointments.component.availabilityCalendar.ariaLabels.futureButton'),
            legend: getLabel('appointments.component.availabilityCalendar.legend')
        })
    , [
        getLabel
    ]);
    const range = useRangeByWeeks(weeks);
    const tileNames = React.useMemo(()=>tileNameLabelsByWeeks(weeks).map((label)=>getLabel(label)
        )
    , [
        getLabel,
        weeks
    ]);
    const tiles = React.useMemo(()=>formatTileData(weeks, availability, closedDates, minEnabledDate, maxEnabledDate, selectedDate)
    , [
        weeks,
        availability,
        minEnabledDate,
        maxEnabledDate,
        selectedDate,
        closedDates
    ]);
    const showPastButton = isDateBefore(minEnabledDate, shiftDate(weeks[0][0], 'day', -1));
    const showFutureButton = maxEnabledDate ? isDateBefore(shiftDate(weeks.slice(-1)[0].slice(-1)[0], 'day', 1), maxEnabledDate) : true;
    const onClickPastButton = React.useCallback(()=>{
        const newWeekDate = shiftDate(weekDate !== null && weekDate !== void 0 ? weekDate : today.current, 'week', -weekCount);
        setWeekDate(newWeekDate);
        const newWeeks = getWeekDaysForWeeksFromDate(newWeekDate, weekCount, weekStartOffset);
        setWeeks(newWeeks);
        onChangeWeeks && onChangeWeeks(newWeeks);
    }, [
        weekDate,
        setWeekDate,
        weekCount,
        weekStartOffset,
        onChangeWeeks
    ]);
    const onClickFutureButton = React.useCallback(()=>{
        const newWeekDate = shiftDate(weekDate !== null && weekDate !== void 0 ? weekDate : today.current, 'week', weekCount);
        setWeekDate(newWeekDate);
        const newWeeks = getWeekDaysForWeeksFromDate(newWeekDate, weekCount, weekStartOffset);
        setWeeks(newWeeks);
        onChangeWeeks && onChangeWeeks(newWeeks);
    }, [
        weekDate,
        setWeekDate,
        weekCount,
        weekStartOffset,
        onChangeWeeks
    ]);
    const onClickTile = React.useCallback((date)=>{
        setSelectedDate(date);
        onSelectDate && onSelectDate(date);
    }, [
        onSelectDate
    ]);
    // Prop values changed
    React.useEffect(()=>setSelectedDate(_selectedDate)
    , [
        _selectedDate
    ]);
    React.useEffect(()=>{
        const newWeekDate = _weekDate !== null && _weekDate !== void 0 ? _weekDate : today.current;
        setWeekDate(_weekDate !== null && _weekDate !== void 0 ? _weekDate : today.current);
        const newWeeks = getWeekDaysForWeeksFromDate(newWeekDate, weekCount, weekStartOffset);
        setWeeks(newWeeks);
        const [[currFirstDay]] = weeks;
        const currLastDay = getLastWeekDay(weeks);
        const [[newFirstDay]] = newWeeks;
        const newLastDay = getLastWeekDay(newWeeks);
        if (isSameDay(currFirstDay, newFirstDay) && isSameDay(currLastDay, newLastDay)) {
            return;
        }
        onChangeWeeks && onChangeWeeks(newWeeks);
    // Run only when _weekDate changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        _weekDate,
        weekCount
    ]);
    // Run at first mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(()=>onChangeWeeks && onChangeWeeks(weeks)
    , []);
    return /*#__PURE__*/ React.createElement(AvailabilityCalendar$1, swcHelpers.extends({}, rest, {
        labels: labels,
        range: range,
        showPastButton: showPastButton,
        showFutureButton: showFutureButton,
        onClickPastButton: onClickPastButton,
        onClickFutureButton: onClickFutureButton,
        onClickTile: onClickTile,
        tileNames: tileNames,
        tiles: tiles,
        loading: loading,
        noTimeSlots: noTimeSlots
    }));
};
var AvailabilityCalendar = AvailabilityCalendarContainer;

export { AvailabilityCalendar as default };
//# sourceMappingURL=AvailabilityCalendarContainer.js.map
